import { useState, useCallback, useMemo } from "react";
import type { Quote, LineItem, ClientInfo, CompanyInfo, Signature, AISuggestion } from "@/types/quote";
import { templates } from "@/data/templates";

function generateId() {
	return Math.random().toString(36).substring(2, 11);
}

function generateQuoteNumber() {
	const now = new Date();
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, "0");
	const seq = String(Math.floor(Math.random() * 9999) + 1).padStart(4, "0");
	return `QC-${year}${month}-${seq}`;
}

const defaultCompany: CompanyInfo = {
	name: "Your Company",
	tagline: "Professional Solutions",
	email: "hello@yourcompany.com",
	phone: "+1 (555) 000-0000",
	address: "123 Business Ave, Suite 100\nCity, State 12345",
	website: "www.yourcompany.com",
	logo: "",
};

const defaultClient: ClientInfo = {
	name: "",
	company: "",
	email: "",
	phone: "",
	address: "",
};

export function useQuote() {
	const [quotes, setQuotes] = useState<Quote[]>([]);
	const [activeQuoteId, setActiveQuoteId] = useState<string | null>(null);
	const [aiSuggestions, setAiSuggestions] = useState<AISuggestion[]>([]);
	const [loadingAi, setLoadingAi] = useState(false);

	const activeQuote = useMemo(() => quotes.find((q) => q.id === activeQuoteId) || null, [quotes, activeQuoteId]);

	const createQuote = useCallback(
		(templateId: string) => {
			const template = templates.find((t) => t.id === templateId);
			if (!template) return;

			const now = new Date();
			const validDate = new Date(now);
			validDate.setDate(validDate.getDate() + 30);

			const newQuote: Quote = {
				id: generateId(),
				templateId,
				title: template.name === "Blank Proposal" ? "Untitled Proposal" : `${template.name} Proposal`,
				quoteNumber: generateQuoteNumber(),
				date: now.toISOString().split("T")[0]!,
				validUntil: validDate.toISOString().split("T")[0]!,
				status: "draft",
				client: { ...defaultClient },
				company: { ...defaultCompany },
				items: template.defaultItems.map((item) => ({
					...item,
					id: generateId(),
				})),
				terms: [...template.defaultTerms],
				notes: template.defaultNotes,
				currency: "USD",
				signature: null,
				createdAt: now.toISOString(),
				updatedAt: now.toISOString(),
			};

			setQuotes((prev) => [...prev, newQuote]);
			setActiveQuoteId(newQuote.id);
			setAiSuggestions([]);
			return newQuote;
		},
		[]
	);

	const updateQuote = useCallback(
		(updates: Partial<Quote>) => {
			if (!activeQuoteId) return;
			setQuotes((prev) =>
				prev.map((q) =>
					q.id === activeQuoteId ? { ...q, ...updates, updatedAt: new Date().toISOString() } : q
				)
			);
		},
		[activeQuoteId]
	);

	const updateClient = useCallback(
		(updates: Partial<ClientInfo>) => {
			if (!activeQuote) return;
			updateQuote({ client: { ...activeQuote.client, ...updates } });
		},
		[activeQuote, updateQuote]
	);

	const updateCompany = useCallback(
		(updates: Partial<CompanyInfo>) => {
			if (!activeQuote) return;
			updateQuote({ company: { ...activeQuote.company, ...updates } });
		},
		[activeQuote, updateQuote]
	);

	const addLineItem = useCallback(() => {
		if (!activeQuote) return;
		const newItem: LineItem = {
			id: generateId(),
			description: "",
			quantity: 1,
			unitPrice: 0,
			discount: 0,
			tax: 0,
		};
		updateQuote({ items: [...activeQuote.items, newItem] });
	}, [activeQuote, updateQuote]);

	const updateLineItem = useCallback(
		(itemId: string, updates: Partial<LineItem>) => {
			if (!activeQuote) return;
			updateQuote({
				items: activeQuote.items.map((item) => (item.id === itemId ? { ...item, ...updates } : item)),
			});
		},
		[activeQuote, updateQuote]
	);

	const removeLineItem = useCallback(
		(itemId: string) => {
			if (!activeQuote) return;
			updateQuote({ items: activeQuote.items.filter((item) => item.id !== itemId) });
		},
		[activeQuote, updateQuote]
	);

	const addTerm = useCallback(
		(term: string) => {
			if (!activeQuote) return;
			updateQuote({ terms: [...activeQuote.terms, term] });
		},
		[activeQuote, updateQuote]
	);

	const removeTerm = useCallback(
		(index: number) => {
			if (!activeQuote) return;
			updateQuote({ terms: activeQuote.terms.filter((_, i) => i !== index) });
		},
		[activeQuote, updateQuote]
	);

	const setSignature = useCallback(
		(signature: Signature | null) => {
			updateQuote({ signature });
		},
		[updateQuote]
	);

	const deleteQuote = useCallback(
		(quoteId: string) => {
			setQuotes((prev) => prev.filter((q) => q.id !== quoteId));
			if (activeQuoteId === quoteId) {
				setActiveQuoteId(null);
			}
		},
		[activeQuoteId]
	);

	const duplicateQuote = useCallback(
		(quoteId: string) => {
			const source = quotes.find((q) => q.id === quoteId);
			if (!source) return;
			const now = new Date();
			const validDate = new Date(now);
			validDate.setDate(validDate.getDate() + 30);
			const newQuote: Quote = {
				...structuredClone(source),
				id: generateId(),
				quoteNumber: generateQuoteNumber(),
				title: `${source.title} (Copy)`,
				status: "draft",
				date: now.toISOString().split("T")[0]!,
				validUntil: validDate.toISOString().split("T")[0]!,
				signature: null,
				createdAt: now.toISOString(),
				updatedAt: now.toISOString(),
			};
			newQuote.items = newQuote.items.map((item) => ({ ...item, id: generateId() }));
			setQuotes((prev) => [...prev, newQuote]);
			setActiveQuoteId(newQuote.id);
		},
		[quotes]
	);

	const calculateTotals = useMemo(() => {
		if (!activeQuote) return { subtotal: 0, totalDiscount: 0, totalTax: 0, total: 0 };
		let subtotal = 0;
		let totalDiscount = 0;
		let totalTax = 0;

		for (const item of activeQuote.items) {
			const lineTotal = item.quantity * item.unitPrice;
			const discountAmount = lineTotal * (item.discount / 100);
			const afterDiscount = lineTotal - discountAmount;
			const taxAmount = afterDiscount * (item.tax / 100);
			subtotal += lineTotal;
			totalDiscount += discountAmount;
			totalTax += taxAmount;
		}

		return {
			subtotal,
			totalDiscount,
			totalTax,
			total: subtotal - totalDiscount + totalTax,
		};
	}, [activeQuote]);

	const fetchPricingSuggestions = useCallback(
		async (description: string, currentPrice: number) => {
			if (!activeQuote) return;
			setLoadingAi(true);
			try {
				const template = templates.find((t) => t.id === activeQuote.templateId);
				const res = await fetch("/api/ai/pricing-suggestions", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						category: template?.category || "service",
						description,
						currentPrice,
					}),
				});
				const data = await res.json();
				setAiSuggestions((prev) => [...prev, ...data.suggestions]);
			} finally {
				setLoadingAi(false);
			}
		},
		[activeQuote]
	);

	const fetchTermsSuggestions = useCallback(async () => {
		if (!activeQuote) return;
		setLoadingAi(true);
		try {
			const template = templates.find((t) => t.id === activeQuote.templateId);
			const res = await fetch("/api/ai/terms-suggestions", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					category: template?.category || "service",
					existingTerms: activeQuote.terms,
				}),
			});
			const data = await res.json();
			setAiSuggestions((prev) => [...prev, ...data.suggestions]);
		} finally {
			setLoadingAi(false);
		}
	}, [activeQuote]);

	const fetchDescriptionSuggestion = useCallback(
		async (description: string) => {
			if (!activeQuote) return;
			setLoadingAi(true);
			try {
				const template = templates.find((t) => t.id === activeQuote.templateId);
				const res = await fetch("/api/ai/description-enhance", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						description,
						category: template?.category || "service",
					}),
				});
				const data = await res.json();
				setAiSuggestions((prev) => [...prev, ...data.suggestions]);
			} finally {
				setLoadingAi(false);
			}
		},
		[activeQuote]
	);

	const dismissSuggestion = useCallback((index: number) => {
		setAiSuggestions((prev) => prev.filter((_, i) => i !== index));
	}, []);

	const clearSuggestions = useCallback(() => {
		setAiSuggestions([]);
	}, []);

	return {
		quotes,
		activeQuote,
		activeQuoteId,
		setActiveQuoteId,
		createQuote,
		updateQuote,
		updateClient,
		updateCompany,
		addLineItem,
		updateLineItem,
		removeLineItem,
		addTerm,
		removeTerm,
		setSignature,
		deleteQuote,
		duplicateQuote,
		calculateTotals,
		aiSuggestions,
		loadingAi,
		fetchPricingSuggestions,
		fetchTermsSuggestions,
		fetchDescriptionSuggestion,
		dismissSuggestion,
		clearSuggestions,
	};
}
